// Org-wide integrations list. Admins-only.
//
// Two routing modes coexist:
//   fixed   — bound 1:1 to a service (chunk 8c flow, also editable
//             from a service's detail page)
//   rules   — provider-level integration that routes inbound alerts
//             to one of many services via tag-matching rules
//             (chunk 9d). Edited on the integration detail page.
//
// The list is the single canonical surface for managing both modes.
// Fixed-mode integrations remain creatable on a service's detail page
// for convenience, but admins should consolidate via this page when
// they have more than a handful.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { onSnapshot, orderBy, query } from 'firebase/firestore';
import { ArrowRight, Plug, Plus } from 'lucide-react';
import { useOrg } from '../contexts/OrgContext';
import { orgIntegrationsCol, orgServicesCol } from '../lib/firestore';
import {
  PROVIDER_LABELS,
  createIntegration,
  readIntegration,
} from '../lib/integrations';
import type { Integration, IntegrationProvider, RoutingMode, Service } from '../lib/types';
import {
  AppShell,
  Badge,
  Button,
  Card,
  CardBody,
  Container,
  FormField,
  Input,
  PageHeader,
  Row,
  Spinner,
  Stack,
  Table,
} from '../components/ui';
import { ErrorBanner } from './SigninPage';

const PROVIDERS: IntegrationProvider[] = [
  'datadog',
  'newrelic',
  'sentry',
  'dynatrace',
  'pagerduty',
  'generic',
];

export default function IntegrationsPage() {
  const { orgId } = useOrg();
  const [integrations, setIntegrations] = useState<Integration[] | null>(null);
  const [services, setServices] = useState<Service[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);

  // Subscribe to both collections in parallel — services feed the
  // service-pickers in the create form and the rules-mode summary.
  useEffect(() => {
    if (!orgId) return;
    const unsubInts = onSnapshot(
      query(orgIntegrationsCol(orgId), orderBy('createdAt', 'asc')),
      (snap) => {
        setIntegrations(
          snap.docs.map((d) =>
            readIntegration(d.data() as Parameters<typeof readIntegration>[0]),
          ),
        );
      },
      (err) => {
        console.error('[integrations] snapshot failed', err);
        setError('Could not load integrations.');
      },
    );
    const unsubServices = onSnapshot(
      query(orgServicesCol(orgId), orderBy('name', 'asc')),
      (snap) => {
        setServices(snap.docs.map((d) => d.data() as Service));
      },
    );
    return () => {
      unsubInts();
      unsubServices();
    };
  }, [orgId]);

  if (!orgId) return null;

  return (
    <AppShell>
      <Container width="wide">
        <PageHeader
          title="Integrations"
          subtitle="Connect Datadog, New Relic, Sentry, and friends. Each integration ingests alerts and turns them into Rouser incidents."
        />

        {error && (
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <ErrorBanner message={error} />
          </div>
        )}

        <Stack gap={6}>
          {showForm ? (
            <CreateIntegrationForm
              orgId={orgId}
              services={services ?? []}
              onCancel={() => setShowForm(false)}
              onCreated={() => setShowForm(false)}
              onError={(m) => setError(m)}
            />
          ) : (
            <Row justify="end">
              <Button
                variant="primary"
                size="sm"
                iconLeft={<Plus size={12} strokeWidth={2} />}
                onClick={() => setShowForm(true)}
              >
                New integration
              </Button>
            </Row>
          )}

          {integrations === null ? (
            <Row gap={2} align="center">
              <Spinner size="sm" />
              <span className="ds-text-muted ds-text-sm">Loading…</span>
            </Row>
          ) : integrations.length === 0 ? (
            <Card>
              <CardBody>
                <Row gap={3} align="center">
                  <Plug size={20} strokeWidth={1.75} color="var(--color-text-tertiary)" />
                  <Stack gap={1}>
                    <span style={{ fontWeight: 'var(--fw-medium)' }}>No integrations yet</span>
                    <span className="ds-text-muted ds-text-sm">
                      Connect a monitoring provider so its alerts show up as Rouser incidents.
                    </span>
                  </Stack>
                </Row>
              </CardBody>
            </Card>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Provider</th>
                  <th>Routing</th>
                  <th>Last fired</th>
                  <th style={{ textAlign: 'right' }}></th>
                </tr>
              </thead>
              <tbody>
                {integrations.map((i) => (
                  <tr key={i.id}>
                    <td style={{ fontWeight: 'var(--fw-medium)' }}>{i.name}</td>
                    <td>
                      <Badge tone="accent">{PROVIDER_LABELS[i.provider]}</Badge>
                    </td>
                    <td>
                      <RoutingSummary integration={i} />
                    </td>
                    <td className="ds-table__mono">
                      {i.lastUsedAt
                        ? new Date(i.lastUsedAt).toLocaleString()
                        : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Link
                        to={`/integrations/${i.id}`}
                        className="ds-button ds-button--ghost ds-button--sm"
                      >
                        Manage
                        <ArrowRight size={12} strokeWidth={2} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Stack>
      </Container>
    </AppShell>
  );
}

function RoutingSummary({ integration }: { integration: Integration }) {
  const mode: RoutingMode = integration.routingMode ?? 'fixed';
  if (mode === 'fixed') {
    return (
      <span className="ds-text-sm">
        <span className="ds-text-muted">Fixed → </span>
        {integration.serviceName || integration.serviceId}
      </span>
    );
  }
  const ruleCount = integration.routingRules?.length ?? 0;
  return (
    <Stack gap={1}>
      <span className="ds-text-sm">
        <span className="ds-text-muted">Rules · </span>
        {ruleCount} rule{ruleCount === 1 ? '' : 's'}
      </span>
      {integration.defaultServiceName && (
        <span className="ds-text-tertiary ds-text-xs">
          Default → {integration.defaultServiceName}
        </span>
      )}
    </Stack>
  );
}

function CreateIntegrationForm({
  orgId,
  services,
  onCancel,
  onCreated,
  onError,
}: {
  orgId: string;
  services: Service[];
  onCancel: () => void;
  onCreated: (integrationId: string) => void;
  onError: (message: string) => void;
}) {
  const [provider, setProvider] = useState<IntegrationProvider>('datadog');
  const [name, setName] = useState('');
  const [routingMode, setRoutingMode] = useState<RoutingMode>('fixed');
  const [fixedServiceId, setFixedServiceId] = useState('');
  const [defaultServiceId, setDefaultServiceId] = useState('');
  const [sentrySecret, setSentrySecret] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const sortedServices = useMemo(
    () => [...services].sort((a, b) => a.name.localeCompare(b.name)),
    [services],
  );

  // For rules mode, the default service is required AT CREATE TIME
  // because rules themselves are added later from the detail page.
  // Without a default and no rules, the integration would silently
  // drop every alert — block the bad state at the form layer.
  const canSubmit =
    name.trim().length > 0 &&
    (routingMode === 'fixed' ? Boolean(fixedServiceId) : Boolean(defaultServiceId)) &&
    (provider !== 'sentry' || sentrySecret.trim().length > 0);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const result = await createIntegration(orgId, {
        provider,
        name: name.trim(),
        routingMode,
        serviceId: routingMode === 'fixed' ? fixedServiceId : undefined,
        defaultServiceId:
          routingMode === 'rules' && defaultServiceId ? defaultServiceId : undefined,
        // Rules mode starts with no rules — admin adds them on the
        // detail page. The default service alone keeps the validator
        // happy while the rule list is empty.
        routingRules: routingMode === 'rules' ? [] : undefined,
        secret: provider === 'sentry' ? sentrySecret.trim() : undefined,
      });
      onCreated(result.integrationId);
      // Send admin straight into detail so they can copy the secret /
      // start adding rules.
      window.location.href = `/integrations/${result.integrationId}`;
    } catch (err) {
      console.error('[integrations] create failed', err);
      onError(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardBody>
        <Stack gap={4} as="form" onSubmit={submit}>
          <Row gap={3} align="center">
            <Plug size={16} strokeWidth={1.75} color="var(--color-accent)" />
            <span style={{ fontWeight: 'var(--fw-medium)' }}>New integration</span>
          </Row>

          <FormField label="Provider">
            {(id) => (
              <select
                id={id}
                className="ds-input"
                value={provider}
                onChange={(e) => setProvider(e.target.value as IntegrationProvider)}
              >
                {PROVIDERS.map((p) => (
                  <option key={p} value={p}>
                    {PROVIDER_LABELS[p]}
                  </option>
                ))}
              </select>
            )}
          </FormField>

          <FormField label="Display name" hint="Internal label — e.g. 'Datadog production'.">
            {(id) => (
              <Input
                id={id}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Datadog production"
                required
                maxLength={80}
                autoFocus
              />
            )}
          </FormField>

          <FormField
            label="Routing"
            hint={
              routingMode === 'fixed'
                ? 'Every alert from this integration creates an incident on the chosen service.'
                : 'Alerts are routed to one of many services based on rules you define on the next screen. Common for org-wide Datadog or Sentry accounts.'
            }
          >
            {() => (
              <Row gap={2}>
                <ModeChoice
                  mode="fixed"
                  current={routingMode}
                  onSelect={() => setRoutingMode('fixed')}
                  title="Single service"
                  description="Bind this integration to one Rouser service."
                />
                <ModeChoice
                  mode="rules"
                  current={routingMode}
                  onSelect={() => setRoutingMode('rules')}
                  title="Multi-service (rules)"
                  description="Route by tag — one integration, many services."
                />
              </Row>
            )}
          </FormField>

          {routingMode === 'fixed' ? (
            <FormField label="Service">
              {(id) => (
                <select
                  id={id}
                  className="ds-input"
                  value={fixedServiceId}
                  onChange={(e) => setFixedServiceId(e.target.value)}
                  required
                >
                  <option value="">Select a service…</option>
                  {sortedServices.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              )}
            </FormField>
          ) : (
            <FormField
              label="Default service"
              hint="Required at create time. Every alert lands here until you add routing rules on the next screen. Once rules cover everything, you can switch the default off — unmatched alerts will then drop with a no-route 202."
            >
              {(id) => (
                <select
                  id={id}
                  className="ds-input"
                  value={defaultServiceId}
                  onChange={(e) => setDefaultServiceId(e.target.value)}
                  required
                >
                  <option value="">Pick a service…</option>
                  {sortedServices.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              )}
            </FormField>
          )}

          {provider === 'sentry' && (
            <FormField
              label="Sentry Client Secret"
              hint="Create an Internal Integration in Sentry first (Settings → Custom Integrations). After saving, copy the generated Client Secret and paste it here. Rouser uses it to verify HMAC signatures."
            >
              {(id) => (
                <Input
                  id={id}
                  type="password"
                  value={sentrySecret}
                  onChange={(e) => setSentrySecret(e.target.value)}
                  placeholder="•••• Sentry Client Secret"
                  required
                />
              )}
            </FormField>
          )}

          <Row gap={2} justify="end">
            <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!canSubmit} loading={submitting}>
              Create integration
            </Button>
          </Row>
        </Stack>
      </CardBody>
    </Card>
  );
}

function ModeChoice({
  mode,
  current,
  onSelect,
  title,
  description,
}: {
  mode: RoutingMode;
  current: RoutingMode;
  onSelect: () => void;
  title: string;
  description: string;
}) {
  const active = mode === current;
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        flex: 1,
        textAlign: 'left',
        padding: 'var(--space-3) var(--space-4)',
        background: active ? 'var(--color-accent-soft)' : 'var(--color-surface)',
        border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        color: active ? 'var(--color-accent-soft-text)' : 'var(--color-text)',
        transition: 'background var(--duration-fast) var(--ease), border-color var(--duration-fast) var(--ease)',
      }}
    >
      <Stack gap={1}>
        <span style={{ fontWeight: 'var(--fw-medium)', fontSize: 'var(--fs-sm)' }}>{title}</span>
        <span className="ds-text-muted ds-text-xs">{description}</span>
      </Stack>
    </button>
  );
}

function extractMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return 'Something went wrong. Try again.';
}
